import { useState } from 'react';
import {
  BUBBLE_MESSAGE_CHARACTER_LIMIT_FOR_ONE_PAGE_COMIC,
  COMIC_TITLE_CHARACTER_LIMIT,
} from '../../constants/globalConstants';
import { ComicStoryboard, EMPTY_STORYBOARD } from './comicTypes';

export type VerificationScore = number | null;
export interface VerificationDetailScores {
  source: VerificationScore;
  claims: VerificationScore;
  language: VerificationScore;
}

export function getOverallVerificationScore(
  scores: VerificationDetailScores,
): VerificationScore {
  const values = Object.values(scores).map(toScore);
  if (values.some((score) => score === null)) return null;
  return Math.round(
    values.reduce<number>((sum, score) => sum + (score ?? 0), 0) /
      values.length,
  );
}

const toScore = (value: number | null | undefined): VerificationScore =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 100
    ? value
    : null;

const withoutVerification = (storyboard: ComicStoryboard): ComicStoryboard => ({
  ...storyboard,
  sourceFidelityScore: null,
  accuracyScore: null,
  languageScore: null,
});

export function useComicStoryboard(source: unknown, language: string) {
  const [storyboard, setStoryboard] =
    useState<ComicStoryboard>(EMPTY_STORYBOARD);
  const [verifiedContext, setVerifiedContext] = useState<{
    source: unknown;
    language: string;
  } | null>(null);

  const acceptStoryboard = (next: ComicStoryboard) => {
    if (next.panels.length !== 4) {
      throw new Error('The storyboard must contain four panels.');
    }
    const title = next.title.slice(0, COMIC_TITLE_CHARACTER_LIMIT);
    setStoryboard(
      title === next.title ? next : withoutVerification({ ...next, title }),
    );
    setVerifiedContext({ source, language });
  };

  const updateText = (index: number, text: string) => {
    setStoryboard((current) => {
      if (!current.panels[index] || current.panels[index].text === text)
        return current;
      return withoutVerification({
        ...current,
        panels: current.panels.map((panel, panelIndex) =>
          panelIndex === index ? { ...panel, text } : panel,
        ),
      });
    });
  };

  const updateTitle = (value: string) => {
    const title = value.slice(0, COMIC_TITLE_CHARACTER_LIMIT);
    setStoryboard((current) =>
      current.title === title
        ? current
        : withoutVerification({ ...current, title }),
    );
  };

  const verificationDetailScores: VerificationDetailScores | null =
    verifiedContext
      ? verifiedContext.source === source &&
        verifiedContext.language === language
        ? {
            source: toScore(storyboard.sourceFidelityScore),
            claims: toScore(storyboard.accuracyScore),
            language: toScore(storyboard.languageScore),
          }
        : { source: null, claims: null, language: null }
      : null;

  return {
    storyboard,
    acceptStoryboard,
    updateText,
    updateTitle,
    verificationDetailScores,
    messages: [storyboard.panels.map((panel) => panel.text)],
    inputErrors: [
      storyboard.panels.map(
        (panel) =>
          panel.text.length > BUBBLE_MESSAGE_CHARACTER_LIMIT_FOR_ONE_PAGE_COMIC,
      ),
    ],
  };
}
